import { Card, CardBody, Flex, useMediaQuery, VStack } from '@chakra-ui/react';
import {
  ListingComparableSetFragment,
  ListingFragment,
  MlsListingObject,
  PropertyDetailsFragment,
  PublicListingFragment,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';
import { ComparableColumn } from '../../Listings/Comparables/ComparableColumns/ComparableColumn';
import {
  getPropertyDetails,
  transformPropertyDetailsToMlsListing,
} from '../../Listings/ListingPage/utils';
import { ExploreProvider } from '../../MapSearch/ExploreContext';
import { ComparablesContainer } from '../styledComponents';
import { CompsMap } from './CompsMap/CompsMap';
import { CompsSummary } from './CompsSummary';

interface ViewCompsProps {
  listing?: ListingFragment | PublicListingFragment;
  propertyDetails?: PropertyDetailsFragment;
  comparableSet?: ListingComparableSetFragment;
  comps: ComparableState[];
  isForPdf?: boolean;
}

export const ViewComps = ({
  listing,
  propertyDetails,
  comparableSet,
  comps,
  isForPdf,
}: ViewCompsProps) => {
  const [isPrint] = useMediaQuery('print');

  const normalizedPropertyDetails = useMemo(
    () => getPropertyDetails(listing, propertyDetails),
    [listing, propertyDetails]
  );

  // If the listing is off market, we need to transform the property details to an MlsListingFragment structure.
  // This is a hack to allow PropertyDetails to be used with the ComparableColumn components.
  const mlsListing = listing
    ? listing.mlsListing
    : transformPropertyDetailsToMlsListing(propertyDetails);

  return (
    <ExploreProvider showClosedStatus skipMlsListingsSearch>
      <VStack gap={isPrint ? 4 : { base: 0, md: 4 }} width="100%">
        <Flex
          direction={isPrint ? 'row' : { base: 'column', md: 'row' }}
          gap={isPrint ? 4 : { base: 0, md: 4 }}
          height={isPrint ? 'auto' : { base: '100%', md: '40vh' }}
          position="relative"
          width="100%"
        >
          <Flex
            display={isPrint || isForPdf ? 'none' : 'flex'}
            flexBasis={isPrint ? 0 : { base: undefined, md: 0 }}
            flexGrow={isPrint ? 1 : { base: undefined, md: 1 }}
            height={isPrint ? 'auto' : { base: '40vh', md: '100%' }}
          >
            <CompsMap
              comparableSet={comparableSet}
              isMini={false}
              mainListing={normalizedPropertyDetails}
            />
          </Flex>
          <Flex flexBasis={0} flexGrow={1} height="100%">
            <Card
              borderRadius={isPrint ? '12px' : { base: '0px', md: '12px' }}
              width="100%"
            >
              <CardBody>
                <CompsSummary
                  comparableSet={comparableSet}
                  listing={normalizedPropertyDetails}
                />
              </CardBody>
            </Card>
          </Flex>
        </Flex>
        <Flex
          pl={isPrint ? 0 : { base: 2, md: 0 }}
          py={isPrint ? 0 : { base: 2, md: 0 }}
          width="100%"
        >
          <ComparablesContainer
            style={
              isPrint
                ? {
                    breakBefore: 'page',
                    marginTop: '30px',
                  }
                : {}
            }
            width="100%"
          >
            <ComparableColumn
              isMainListing
              isEditMode={false}
              isForPdf={isForPdf}
              mainMlsListing={mlsListing as MlsListingObject}
              mlsListing={mlsListing as MlsListingObject}
              showAdjustments={true}
              showNotes={true}
            />
            {comps.map((comp) => (
              <ComparableColumn
                key={comp.mlsListing.id}
                comparableSet={comparableSet}
                isEditMode={false}
                isForPdf={isForPdf}
                isMainListing={false}
                mainMlsListing={mlsListing as MlsListingObject}
                mlsListing={comp.mlsListing as MlsListingObject}
                showAdjustments={true}
                showNotes={true}
              />
            ))}
          </ComparablesContainer>
        </Flex>
      </VStack>
    </ExploreProvider>
  );
};
